import React from 'react';
import styled, { css } from 'styled-components/macro';
import { CustomScroll } from '@yme/atoms/custom-scroll';
import { SVGIcon } from '@yme/atoms/svg-icon';
import { colorAccentError700, colorAccentError800Base, colorBrandYamahaRed, colorCoreBlack200, colorCoreBlack300, colorCoreBlack900Base, colorCoreGray300, colorCoreGray500, colorCoreGray800, colorCoreWhite1000, colorCoreGray1000Base, } from '@yme/tokens/colors';
import { CORNER_RADIUS } from '@yme/tokens/cornerRadius';
import { elevationBottomLvl2 } from '@yme/tokens/elevations';
import { ADAPTIVE } from '@yme/tokens/screen';
import { INDENT } from '@yme/tokens/sizes';
import { Z_INDEX } from '@yme/tokens/zindex';
import { ellipsisEndingLine, getTextStyles } from '@yme/utils/styles';
export const DropdownStyled = styled.div `
  position: relative;
  cursor: ${({ isDisabled }) => (isDisabled ? 'not-allowed' : 'pointer')};
  background-color: transparent;
  -webkit-tap-highlight-color: transparent;
`;
export const ArrowStyled = styled.div `
  display: flex;
  align-items: center;
  position: absolute;

  top: calc(50% - 22px);
  height: 44px;
  width: 34px;

  ${({ isRightToLeft }) => (isRightToLeft ? 'left: 0px;' : 'right: 0;')}
`;
export const TogglerStyled = styled.div `
  ${getTextStyles('Ubuntu', 400, 14, 20)}

  box-sizing: border-box;
  border: 1px solid ${({ isListOpen }) => (isListOpen ? colorCoreBlack900Base : colorCoreGray500)};
  position: relative;
  border-radius: ${CORNER_RADIUS.s};

  ${ellipsisEndingLine};

  min-height: 44px;
  display: flex;
  padding: ${({ paddingValue }) => paddingValue || '0px 12px'};
  gap: 8px;
  align-items: center;

  pointer-events: ${({ isDisabled }) => (isDisabled ? 'none' : 'auto')};
  color: ${({ isDisabled }) => (isDisabled ? colorCoreBlack200 : colorCoreBlack300)};
  background-color: ${({ isInheritBackground }) => isInheritBackground ? 'inherit' : colorCoreWhite1000};

  input,
  span {
    border: none;
    height: ${({ isFlexible }) => (isFlexible ? 'auto' : '36px')};
    align-items: center;
    display: flex;
    column-gap: 10px;
    &:focus {
      outline: none;
    }
  }

  input {
    background-color: ${({ isInheritBackground }) => isInheritBackground && 'inherit'};
    ${ellipsisEndingLine}
  }

  input:disabled {
    color: ${colorCoreBlack900Base};
    background-color: transparent;
  }

  input {
    width: calc(100% - 52px);
    padding: 0 0 2px 0;
    ${getTextStyles('Ubuntu', 400, 14, 20)}
  }

  ${({ error }) => error &&
    css `
      border-color: ${colorAccentError700};
    `}

  &:hover {
    border-color: ${colorCoreGray800};
  }
`;
export const ListStyled = styled.ul `
  position: absolute;
  width: 100%;
  box-sizing: border-box;
  display: flex;
  visibility: ${({ isListOpen }) => (isListOpen ? 'visible' : 'hidden')};
  flex-direction: column;
  align-items: stretch;
  list-style-type: none;

  ${CustomScroll};

  box-shadow: ${elevationBottomLvl2};
  background-color: ${colorCoreWhite1000};
  border-radius: ${CORNER_RADIUS.m};
  padding: ${({ hasScroll, isRightToLeft }) => {
    if (!hasScroll)
        return '0';
    return isRightToLeft ? '0 0 0 6px' : '0 6px 0 0';
}};
  /* TODO: (D.K.) need to add styles for border */

  z-index: ${Z_INDEX.popupLevel2};

  max-height: 22vh;
  margin: 10px 0 0;

  ${({ isUpwardDirection }) => isUpwardDirection &&
    css `
      bottom: 0;
      margin: 0 0 54px;
    `};
`;
export const ListStyledMobile = styled.div `
  ${CustomScroll};

  ${({ isRightToLeft }) => isRightToLeft && 'direction: rtl;'}
  overflow-y: auto;
  max-height: 332px;
`;
export const ListItemStyled = styled.li `
  ${getTextStyles('Ubuntu', 400, 14, 20)}

  height: ${({ isFlexible }) => (isFlexible ? 'auto' : '40px')};
  display: flex;
  align-items: center;
  padding: 8px 12px;
  gap: 10px;
  box-sizing: border-box;

  color: ${colorCoreBlack900Base};
  border-radius: ${CORNER_RADIUS.xs};
  ${({ isActiveItem }) => isActiveItem && `background-color: ${colorCoreGray300}`};

  &:hover {
    background-color: ${colorCoreGray300};
  }

  & > span {
    display: flex;
    align-items: center;
    column-gap: 10px;
  }
`;
export const ActiveItemStyled = styled.div `
  color: ${({ isDisabled }) => (isDisabled ? colorCoreGray1000Base : colorCoreBlack900Base)};
  display: ${({ contents }) => (contents ? 'contents' : 'block')};
  line-height: 26px;

  ${({ isRightToLeft }) => isRightToLeft ? `padding-left: ${INDENT.sm}` : `padding-right: ${INDENT.sm}`};

  ${ellipsisEndingLine}

  &.has-icon {
    > div[size],
    > span[size] {
      float: left;
      margin: 0 8px 0 0;
    }
  }
`;
export const NoResultsStyled = styled.span `
  ${getTextStyles('Ubuntu', 400, 14, 20)}
  color: ${colorCoreBlack900Base};
`;
export const LabelStyled = styled.label `
  ${getTextStyles('Ubuntu', 500, 14, 20)}

  display: inline-block;
  margin-bottom: 8px;
  color: ${colorCoreBlack900Base};

  ${({ isRequired }) => isRequired &&
    `
    &::after {
      color: ${colorBrandYamahaRed};
    }   
    &::after {
      content: "*";     
    }`};

  ${({ isRequired, isRightToLeft }) => isRequired &&
    (isRightToLeft ? '&::after {  margin-right: 2px; }' : '&::after {  margin-left: 2px; }')};
`;
export const ErrorMessage = styled.span `
  ${getTextStyles('Ubuntu', 400, 12, 16)}

  display: ${({ error }) => (error ? 'block' : 'none')};
  margin-top: 10px;
  color: ${colorAccentError800Base};
`;
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const SVGIconStyled = styled(({ isExpandedDropDown, ...props }) => React.createElement(SVGIcon, { ...props })) `
  ${({ isExpandedDropDown }) => isExpandedDropDown ? 'transform: rotate(0deg);' : 'transform: rotate(180deg);'}
`;
export const ListItemRadioStyled = styled.li `
  list-style-type: none;
  padding: 10px 8px;

  ${ADAPTIVE.minWidth.tablet} {
    padding: 12px 16px;
  }
`;
